import React, { Component } from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'

import { selectUser } from '../../reducers'
import { UserBanner } from '../users/userBanner'
import UserAccount from './userAccount'
import UserVerifications from './userVerifications'
import UserBankAccounts from './userBankAccounts'
import UserVehicles from './userVehicles'
import UserUnderwriting from './userUnderwriting'
import UserNotes from './userNotes'
import {
  resendConfirmationEmail,
  sendResetPasswordEmail,
  deleteBankAccount,
} from './userActions'
import { fetchUser, fetchDealsForSync } from './userActions'
import LoadingSection from '../../components/loadingSection'
import InfoIcon from '../../assets/images/info.svg'
import StatusBanner from '../common/statusBanner'
import { User } from '../../core/types'
import Breadcrumb from '../../components/Breadcrumb'
import { parseQuery, stringifyQuery } from '../../helpers/queryHelper'
import { userAddressPath } from '../../helpers/linkHelper'
import CallToolsDealsModal, {
  DealPackage,
  newDealSearch,
} from './CallToolsDealsModal'

export type Callback<T = void> = (...args: any) => T
export interface Match<T> {
  params: T
}

export interface ParamsID {
  id: string
}

export interface Props {
  user: User
  match: Match<ParamsID>
  location: {
    search: string
    pathname: string
  }
  history: {
    replace: (path: string) => void
    push: (path: string) => void
  }
  fetchUser: (...args: any) => Promise<any>
  fetchDealsForSync: (args: {
    vehicleId: string
    userId: string
  }) => Promise<DealPackage[]>
  deleteBankAccount: Callback
  resendConfirmationEmail: Callback
  sendResetPasswordEmail: Callback
}

interface State {
  loading: boolean
  showDeals: boolean
  deals: DealPackage[]
}

export class UserProfile extends Component<Props, State> {
  state: Readonly<State> = { loading: false, showDeals: false, deals: [] }

  componentDidMount() {
    this.mountedRef.current = true
    this.fetchUserData()
    this.clearResyncErrorParam()
    this.openDealsModalFromQuery()
  }

  componentDidUpdate(prevProps) {
    const { match } = this.props
    if (match.params.id !== prevProps.match.params.id) {
      this.fetchUserData()
    }
  }

  componentWillUnmount() {
    this.mountedRef.current = false
  }

  mountedRef: React.MutableRefObject<boolean> = { current: false }

  fetchUserData = () => {
    const { fetchUser, match } = this.props
    this.setState({ loading: true })
    const p = fetchUser(match.params.id)

    // safely set state only if still mounted
    const setStateIfMounted = (state) => {
      if (this.mountedRef.current) {
        this.setState(state)
      }
    }

    p.then(() => setStateIfMounted({ loading: false })).catch(() =>
      setStateIfMounted({ loading: false })
    )
    return p
  }

  // The webhook still appends ?reSyncError=<status> when it could not rewrite
  // the deal, but the banner is gone: the deals modal opens on the same
  // redirect and already shows every deal with its status and what can be done
  // with it, so the banner only restated it as an error the agent cannot act
  // on. The param is still stripped so it does not survive a reload.
  clearResyncErrorParam = () => {
    const { location, history } = this.props
    const parsedQuery = parseQuery(location.search)

    if (!parsedQuery.reSyncError) return

    delete parsedQuery.reSyncError
    const nextQuery = stringifyQuery(parsedQuery)
    history.replace(
      nextQuery ? `${location.pathname}?${nextQuery}` : location.pathname
    )
  }

  // The CallTools webhook lands here with ?vehicleId=&showDeals=1 when the
  // synced vehicle already carries deals, so the agent picks one rather than
  // the sync guessing.
  //
  // Two sets are loaded and merged: every deal on the synced vehicle whoever
  // owns it, plus this customer's deals on their other vehicles. The webhook
  // matches the vehicle org-wide
  // (Crm::Inbound::ContactsService#matching_product_packages) but can only land
  // the agent on one customer, so deals owned by other customers would
  // otherwise be missing — and the agent also needs the customer's own book.
  openDealsModalFromQuery = () => {
    const { location, match, fetchDealsForSync } = this.props
    const { showDeals, vehicleId } = parseQuery(location.search)

    if (!showDeals || !vehicleId) return

    this.setState({ showDeals: true })

    fetchDealsForSync({
      vehicleId: String(vehicleId),
      userId: match.params.id,
    }).then((deals) => {
      if (this.mountedRef.current) {
        this.setState({ deals: deals || [] })
      }
    })
  }

  closeDealsModal = () => {
    const { location, history } = this.props
    const parsedQuery = parseQuery(location.search)

    this.setState({ showDeals: false, deals: [] })

    delete parsedQuery.showDeals
    delete parsedQuery.vehicleId
    const nextQuery = stringifyQuery(parsedQuery)
    history.replace(
      nextQuery ? `${location.pathname}?${nextQuery}` : location.pathname
    )
  }

  continueApplication = (productPackage: DealPackage) => {
    this.props.history.push(`/refiapp?packageId=${productPackage.id}`)
  }

  // None of the listed deals is the one the agent is calling about. Open the
  // wizard on this customer carrying the synced vehicle's VALUES, so the
  // Vehicle step is prefilled but bound to nothing: editing the details there
  // creates a vehicle rather than rewriting the one these deals share. The
  // deal itself is created when the agent submits that step, and with no
  // packageId the wizard takes its createProductPackage branch.
  // The synced vehicle, not whatever sits at the top of the list: the merged
  // list also carries deals on the customer's other cars, so row 0 is routinely
  // a different vehicle. Loading by vehicleId guarantees a row for it whenever
  // the vehicle has any deal at all.
  syncedVehicle = () => {
    const { location } = this.props
    const { vehicleId } = parseQuery(location.search)

    return this.state.deals.find(
      (deal) => String(deal.vehicle?.id) === String(vehicleId)
    )?.vehicle
  }

  startNewDeal = () => {
    const { history, match } = this.props

    history.push(
      `/refiapp?${newDealSearch(match.params.id, this.syncedVehicle())}`
    )
  }

  deleteAccountOnClick = (id) => {
    const { deleteBankAccount } = this.props
    if (window.confirm('Delete bank account?')) {
      deleteBankAccount(id)
    }
  }

  render() {
    const { user, resendConfirmationEmail, sendResetPasswordEmail } = this.props
    const { deals } = this.state

    const levels = [
      { label: 'Index', path: '/' },
      { label: 'People', path: '/users' },
      {
        label: this.state.loading
          ? 'Loading...'
          : `${user.firstName} ${user.lastName}`,
      },
    ]

    return (
      <>
        <Breadcrumb levels={levels} />
        <LoadingSection isLoading={this.state.loading}>
          {!this.state.loading && user?.id && (
            <div className="user-profile">
              {!user.enabled ? (
                <StatusBanner
                  icon={InfoIcon}
                  status="DISABLED"
                  text={'This user has been disabled.'}
                />
              ) : null}
              {user.fraud ? (
                <StatusBanner
                  icon={InfoIcon}
                  status="FRAUD"
                  text={'This user was marked as fraudulent.'}
                />
              ) : null}
              {user.addressNeeded ? (
                <StatusBanner
                  icon={InfoIcon}
                  status="ADDRESS_NEEDED"
                  text="Address needed. Add the customer's address before signing or booking."
                >
                  {' '}
                  <Link
                    to={userAddressPath(user)}
                    style={{ color: 'inherit', textDecoration: 'underline' }}
                    data-testid="address-needed-link"
                  >
                    Add address
                  </Link>
                </StatusBanner>
              ) : null}
              <UserBanner
                user={user}
                context="User"
                wrapperClass="user-profile__banner"
              />
              <div className="user-profile__col user-profile__section user-profile__row-1 user-profile__section--account">
                <UserAccount
                  user={user}
                  resendConfirmationEmail={resendConfirmationEmail}
                  sendResetPasswordEmail={sendResetPasswordEmail}
                />
                <UserVerifications user={user} />
              </div>
              <div className="user-profile__col user-profile__section user-profile__row-1 user-profile__section--finance">
                <UserUnderwriting user={user} />
                <UserBankAccounts
                  user={user}
                  deleteAccount={this.deleteAccountOnClick}
                />
              </div>
              <div className="user-profile__col user-profile__section user-profile__section--vehicles">
                <UserVehicles user={user} />
              </div>
              <div className="user-profile__col user-profile__section user-profile__row-2 user-profile__wide user-profile__section--notes">
                <UserNotes user={user} />
              </div>
              <CallToolsDealsModal
                isOpen={this.state.showDeals && deals.length > 0}
                onClose={this.closeDealsModal}
                packages={deals}
                syncedVehicleId={this.syncedVehicle()?.id}
                onContinueApplication={this.continueApplication}
                onStartNewDeal={this.startNewDeal}
              />
            </div>
          )}
        </LoadingSection>
      </>
    )
  }
}

export const mapStateToProps = (state, { match }) => {
  return {
    user: selectUser(state.users, match.params.id),
  }
}

export default connect(mapStateToProps, {
  fetchUser,
  fetchDealsForSync,
  resendConfirmationEmail,
  sendResetPasswordEmail,
  deleteBankAccount,
})(UserProfile)
